import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import config from '../config';
import fetcher from "../context/fetcher";

export default class AnalyticsApp extends Component {
    constructor(props) {
        super(props);
        this.state = {
            app: null,
            status: '',
            error: '',
            isTesting: false,
            isIconUploading: false,
            isCrawlerRunning: false,
            testResult: null
        };

        this.getApp = this.getApp.bind(this);
        this.saveApp = this.saveApp.bind(this);
        this.deleteApp = this.deleteApp.bind(this);
        this.runTestEvent = this.runTestEvent.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.copyAppId = this.copyAppId.bind(this);
        this.uploadIcon = this.uploadIcon.bind(this);
        this.runCrawler = this.runCrawler.bind(this);
    }

    getS3FilesUrl() {
        const app = this.state.app;
        if (!app)
            return '';

        const pathParts = (app.s3Path || app.appId || '')
            .trim()
            .replace(/\\/g, '/')
            .split('/')
            .filter(x => x);

        if (pathParts[0] && pathParts[0].toLowerCase() === 'analytics')
            pathParts.shift();

        const path = pathParts
            .map(encodeURIComponent)
            .join('/');

        return path ? `${window.location.origin}/Files/${path}/` : '';
    }

    componentDidMount() {
        this.getApp();
    }

    getApp() {
        const appId = this.props.match.params.appId;
        fetcher(`${config.API()}analytics-apps/${encodeURIComponent(appId)}`)
            .then(response => {
                if (!response.ok)
                    throw new Error('Analytics app not found');
                return response.json();
            })
            .then(app => this.setState({app, error: ''}))
            .catch(error => this.setState({error: error.message, app: null}));
    }

    handleChange(field) {
        return (e) => {
            const value = field === 'enabled' || field === 'crawlerEnabled' ? e.target.checked : e.target.value;
            this.setState({
                app: {
                    ...this.state.app,
                    [field]: value
                }
            });
        };
    }

    saveApp(e) {
        e.preventDefault();
        const app = this.state.app;
        fetcher(`${config.API()}analytics-apps/${encodeURIComponent(app.appId)}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                name: app.name,
                enabled: app.enabled,
                crawlerEnabled: app.crawlerEnabled !== false,
                dashboardUrl: app.dashboardUrl,
                iconUrl: app.iconUrl,
                s3Path: app.s3Path,
                athenaDatabase: app.athenaDatabase
            })
        })
            .then(response => response.json().then(payload => {
                if (!response.ok)
                    throw new Error(payload.error || payload.msg || 'Save failed');
                return payload;
            }))
            .then(app => {
                const botStatus = app.analyticsBotSync && app.analyticsBotSync.ok === false
                    ? `Dashboard sync: ${app.analyticsBotSync.error}`
                    : 'Saved and dashboards synced';
                this.setState({app, status: botStatus});
            })
            .catch(error => this.setState({status: error.message}));
    }

    deleteApp(e) {
        e.preventDefault();
        if (!window.confirm('Delete analytics app?'))
            return;

        const app = this.state.app;
        fetcher(`${config.API()}analytics-apps/${encodeURIComponent(app.appId)}`, {
            method: 'DELETE'
        })
            .then(response => response.json().catch(() => ({})).then(payload => {
                if (!response.ok)
                    throw new Error(payload.error || payload.msg || 'Delete failed');
                if (payload.analyticsBotSync && payload.analyticsBotSync.ok === false)
                    window.alert(`App deleted, but dashboard sync failed: ${payload.analyticsBotSync.error}`);
                this.props.history.push('/analytics/apps');
            }))
            .catch(error => this.setState({status: error.message}));
    }

    runTestEvent(e) {
        e.preventDefault();
        const app = this.state.app;
        this.setState({isTesting: true, status: 'Running test event...', testResult: null});
        fetcher(`${config.API()}analytics-test/${encodeURIComponent(app.appId)}/event`, {
            method: 'POST'
        })
            .then(response => response.json().catch(() => ({})).then(payload => {
                if (!response.ok && response.status !== 202)
                    throw new Error(payload.error || payload.msg || payload.message || 'Test event failed');
                return payload;
            }))
            .then(result => {
                this.setState({
                    isTesting: false,
                    testResult: result,
                    status: result.message || (result.ok ? 'Test event completed' : 'Test event accepted')
                });
            })
            .catch(error => this.setState({
                isTesting: false,
                status: error.message,
                testResult: {ok: false, message: error.message}
            }));
    }

    copyAppId(e) {
        e.preventDefault();
        if (navigator.clipboard && this.state.app) {
            navigator.clipboard.writeText(this.state.app.appId);
        }
        this.setState({status: 'Copied app_id'});
    }

    uploadIcon(e) {
        const file = e.target.files && e.target.files[0];
        const app = this.state.app;
        if (!file || !app)
            return;

        const extMatch = file.name.match(/\.[a-z0-9]+$/i);
        const fileName = `icon${extMatch ? extMatch[0].toLowerCase() : ''}`;
        const prefix = encodeURI(app.appId);
        this.setState({isIconUploading: true, status: 'Uploading icon...'});

        fetcher(`${config.API()}files-s3/presign/${prefix}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                fileName,
                contentType: file.type || undefined,
                cacheControl: 'max-age=0, must-revalidate'
            })
        })
            .then(response => response.json())
            .then(presign => {
                if (!presign.url || !presign.headers || !presign.publicUrl)
                    throw new Error('S3 upload URL is incomplete');

                return fetch(presign.url, {
                    method: 'PUT',
                    headers: presign.headers,
                    body: file
                }).then(response => {
                    if (!response.ok)
                        throw new Error(`S3 upload failed: ${response.status}`);

                    const iconUrl = `${presign.publicUrl}?v=${Date.now()}`;
                    this.setState({
                        app: {
                            ...this.state.app,
                            iconUrl
                        },
                        isIconUploading: false,
                        status: 'Icon uploaded. Save changes.'
                    });
                });
            })
            .catch(error => this.setState({
                isIconUploading: false,
                status: error.message || 'Icon upload failed'
            }))
            .finally(() => {
                e.target.value = '';
            });
    }

    formatCrawlerLastRun() {
        const app = this.state.app;
        if (!app || !app.crawlerLastRunAt)
            return '';

        return new Date(app.crawlerLastRunAt).toLocaleString();
    }

    runCrawler(e) {
        e.preventDefault();
        const app = this.state.app;
        if (!app)
            return;

        this.setState({isCrawlerRunning: true, status: 'Starting crawler...'});
        fetcher(`${config.API()}analytics-apps/${encodeURIComponent(app.appId)}/crawler/run`, {
            method: 'POST'
        })
            .then(response => response.json().catch(() => ({})).then(payload => {
                if (!response.ok)
                    throw new Error(payload.error || payload.msg || 'Crawler run failed');
                return payload;
            }))
            .then(app => {
                const run = app.crawlerRun || {};
                const status = run.started === false
                    ? 'Crawler is already running'
                    : 'Crawler started';
                this.setState({app, isCrawlerRunning: false, status});
            })
            .catch(error => this.setState({
                isCrawlerRunning: false,
                status: error.message
            }));
    }

    render() {
        const app = this.state.app;
        if (this.state.error) {
            return (
                <div className="card shadow w100">
                    <div className="card-header py-3">
                        <h2 className="m-0 font-weight-bold l"> Analytics App </h2>
                    </div>
                    <div className="card-body">
                        <div className="alert alert-danger">{this.state.error}</div>
                        <Link className="btn btn-secondary" to="/analytics/apps">Back</Link>
                    </div>
                </div>
            );
        }

        if (!app) {
            return (
                <div className="card shadow w100">
                    <div className="card-header py-3">
                        <h2 className="m-0 font-weight-bold l"> Analytics App </h2>
                    </div>
                    <div className="card-body">Loading...</div>
                </div>
            );
        }

        return (
            <div className="card shadow w100">
                <div className="card-header py-3 d-flex align-items-center justify-content-between">
                    <h2 className="m-0 font-weight-bold l"> Analytics App </h2>
                    <Link className="btn btn-secondary btn-sm" to="/analytics/apps">
                        <i className="fa fa-arrow-left mr-1"></i> Back
                    </Link>
                </div>
                <div className="card-body">
                    <form onSubmit={this.saveApp}>
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">app_id</label>
                            <div className="col-sm-10 d-flex align-items-center">
                                <code>{app.appId}</code>
                                <button className="btn btn-outline-primary btn-circle btn-sm ml-2" onClick={this.copyAppId}>
                                    <i className="fa fa-copy"></i>
                                </button>
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Name</label>
                            <div className="col-sm-10">
                                <input type="text"
                                       className="form-control"
                                       value={app.name || ''}
                                       onChange={this.handleChange('name')}
                                />
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Icon</label>
                            <div className="col-sm-10">
                                <div className="d-flex align-items-center">
                                    <div className="mr-3 d-flex align-items-center justify-content-center"
                                         style={{width: '56px', height: '56px', border: '1px solid #d8dde8', borderRadius: '6px', overflow: 'hidden'}}>
                                        {app.iconUrl
                                            ? <img src={app.iconUrl} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
                                            : <i className="fa fa-image text-muted"></i>}
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="input-group mb-2">
                                            <input type="text"
                                                   className="form-control"
                                                   placeholder="https://..."
                                                   value={app.iconUrl || ''}
                                                   onChange={this.handleChange('iconUrl')}
                                            />
                                            {app.iconUrl && <div className="input-group-append">
                                                <a className="btn btn-outline-primary"
                                                   href={app.iconUrl}
                                                   target="_blank"
                                                   rel="noreferrer">
                                                    Open
                                                </a>
                                            </div>}
                                        </div>
                                        <input type="file"
                                               accept="image/*"
                                               disabled={this.state.isIconUploading}
                                               onChange={this.uploadIcon}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Dashboard URL</label>
                            <div className="col-sm-10">
                                <div className="input-group">
                                    <input type="text"
                                           className="form-control"
                                           placeholder="https://codexbingo.galaxy4gm.com/"
                                           value={app.dashboardUrl || ''}
                                           onChange={this.handleChange('dashboardUrl')}
                                    />
                                    {app.dashboardUrl && <div className="input-group-append">
                                        <a className="btn btn-outline-primary"
                                           href={app.dashboardUrl}
                                           target="_blank"
                                           rel="noreferrer">
                                            Open
                                        </a>
                                    </div>}
                                </div>
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">S3 path</label>
                            <div className="col-sm-10">
                                <div className="input-group">
                                    <input type="text"
                                           className="form-control"
                                           placeholder={`analytics/${app.appId}`}
                                           value={app.s3Path || ''}
                                           onChange={this.handleChange('s3Path')}
                                    />
                                    {this.getS3FilesUrl() && <div className="input-group-append">
                                        <a className="btn btn-outline-primary"
                                           href={this.getS3FilesUrl()}
                                           target="_blank"
                                           rel="noreferrer">
                                            Open
                                        </a>
                                    </div>}
                                </div>
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Glue database</label>
                            <div className="col-sm-10">
                                <input type="text"
                                       className="form-control"
                                       value={app.athenaDatabase || ''}
                                       onChange={this.handleChange('athenaDatabase')}
                                />
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Crawler</label>
                            <div className="col-sm-10 d-flex align-items-center">
                                <label className="mb-0 mr-4">
                                    <input type="checkbox"
                                           className="mr-2"
                                           checked={app.crawlerEnabled !== false}
                                           onChange={this.handleChange('crawlerEnabled')}
                                    />
                                    {app.crawlerEnabled === false ? 'Off' : 'On'}
                                </label>
                                <span className="mr-3">{app.crawlerName || ''}</span>
                                {this.formatCrawlerLastRun() && <span className="text-muted mr-3">
                                    Last run: {this.formatCrawlerLastRun()}
                                </span>}
                                <button className="btn btn-outline-primary btn-sm"
                                        type="button"
                                        disabled={this.state.isCrawlerRunning || app.crawlerEnabled === false}
                                        onClick={this.runCrawler}>
                                    Run
                                </button>
                            </div>
                        </div>
                        {app.s3TargetPath && <div className="form-group row">
                            <label className="col-sm-2 col-form-label">S3 target</label>
                            <div className="col-sm-10 d-flex align-items-center">
                                {app.s3TargetPath}
                            </div>
                        </div>}
                        {app.crawlerError && <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Crawler error</label>
                            <div className="col-sm-10">
                                <div className="alert alert-warning mb-0">{app.crawlerError}</div>
                            </div>
                        </div>}
                        {this.state.testResult && <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Test event</label>
                            <div className="col-sm-10">
                                <div className={`alert mb-0 ${this.state.testResult.ok ? 'alert-success' : 'alert-warning'}`}>
                                    <div>{this.state.testResult.message}</div>
                                    {this.state.testResult.s3ObjectKey && <div>S3: <code>{this.state.testResult.s3ObjectKey}</code></div>}
                                    {this.state.testResult.expectedS3Prefix && <div>S3: <code>{this.state.testResult.expectedS3Prefix}</code></div>}
                                    {this.state.testResult.glueTable && <div>Glue table: <code>{this.state.testResult.glueTable.name}</code></div>}
                                </div>
                            </div>
                        </div>}
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Enabled</label>
                            <div className="col-sm-10 d-flex align-items-center">
                                <label className="mb-0">
                                    <input type="checkbox"
                                           className="mr-2"
                                           checked={!!app.enabled}
                                           onChange={this.handleChange('enabled')}
                                    />
                                    {app.enabled ? 'On' : 'Off'}
                                </label>
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Created</label>
                            <div className="col-sm-10 d-flex align-items-center">
                                {app.createdAt ? new Date(app.createdAt).toLocaleString() : ''}
                            </div>
                        </div>
                        <div className="form-group row">
                            <label className="col-sm-2 col-form-label">Users</label>
                            <div className="col-sm-10 d-flex align-items-center">
                                {app.usersCount || 0}
                            </div>
                        </div>
                        <button className="btn btn-icon btn-primary" type="submit">
                            <i className="fa fa-save"></i> Save
                        </button>
                        <button className="btn btn-danger ml-2" onClick={this.deleteApp}>
                            Delete
                        </button>
                        <button className="btn btn-outline-primary ml-2"
                                type="button"
                                disabled={this.state.isTesting}
                                onClick={this.runTestEvent}>
                            Test event
                        </button>
                        {this.state.status && <span className="ml-3 text-muted">{this.state.status}</span>}
                    </form>
                </div>
            </div>
        );
    }
}
